import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  School,
  Calendar,
  Award,
  Users,
  GraduationCap,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Brain,
  BookOpen,
  BarChart3,
  TrendingUp,
  FileText,
  Building2,
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, currentOrg, organizations, setCurrentOrg, isSuperAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // YÖMEV user kontrolü
  const isYomevUser = user?.email?.endsWith('@yomev.org');

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: School, label: 'Okullar', path: '/organizations', adminOnly: true },
    ...(currentOrg
      ? [
          { icon: Calendar, label: 'Program & Planlama', path: `/org/${currentOrg.id}/planning` },
          { icon: Award, label: 'Akreditasyon', path: `/org/${currentOrg.id}/accreditation` },
        ]
      : []),
    { icon: Brain, label: '🤖 Plan Studio (AI)', path: '/plan-studio', yomevOnly: true },
    { icon: Brain, label: '🤖 AI Haftalık Planlar', path: '/weekly-plans', aiOnly: true },
    { icon: BookOpen, label: '📚 Doküman Kütüphanesi', path: '/library' },
    { icon: Users, label: 'Community', path: '/community' },
    { icon: GraduationCap, label: 'Online Eğitim', path: '/learning' },
    { icon: TrendingUp, label: '📈 İlerlemem', path: '/learning/my-progress' },
    { icon: FileText, label: '📝 Quiz & Testlerim', path: '/my-quizzes' },
    { icon: Award, label: '🏅 Sertifikasyonum', path: '/my-certification' },
    { icon: Building2, label: '🏛️ Akreditasyon Panosu', path: '/accreditation-dashboard' },
    ...(isYomevUser ? [
      { icon: GraduationCap, label: '🎓 Online Eğitimlerim', path: '/yomev/courses', yomevOnly: true },
      { icon: Settings, label: '⚙️ Kütüphane Yönetimi', path: '/admin/library', yomevOnly: true },
      { icon: BarChart3, label: '📊 Analitik', path: '/analytics', yomevOnly: true }
    ] : []),
    { icon: Settings, label: 'Admin Panel', path: '/admin', adminOnly: true },
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Topbar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50 flex items-center px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 mr-4"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className="flex items-center gap-3">
          <img src="/logos/ilkadim360-color.jpeg" alt="İlkadım360 Hub" className="h-8" />
        </div>

        <div className="ml-auto flex items-center gap-4">
          {/* Organization Switcher */}
          {organizations.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <span className="text-sm font-medium">{currentOrg?.name || 'Okul Seç'}</span>
                <ChevronDown size={16} />
              </button>
              {orgDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {organizations.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => {
                        setCurrentOrg(org);
                        setOrgDropdownOpen(false);
                        navigate('/dashboard');
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                        currentOrg?.id === org.id ? 'bg-blue-50 text-blue-600' : ''
                      }`}
                    >
                      <div className="font-medium">{org.name}</div>
                      <div className="text-xs text-gray-500">{org.city}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* User Menu */}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-xs text-gray-500">{user?.email}</div>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-lg hover:bg-red-50 text-red-600"
              title="Çıkış Yap"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-gray-200 transition-transform duration-300 z-40 overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            if (item.adminOnly && !isSuperAdmin()) return null;
            if (item.yomevOnly && !isYomevUser) return null;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-50 text-blue-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          sidebarOpen ? 'ml-64' : 'ml-0'
        } mt-16 p-6`}
      >
        {children}
      </div>
    </div>
  );
};

export default Layout;
